import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, UIMessage } from "ai";
import { useMemo, useState, type KeyboardEventHandler } from "react";
import { extractChatErrorMessage } from "../../client-shared/api/chat-error.utils";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    TextField,
    Tooltip,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Refresh from "@mui/icons-material/Refresh";
import { getUrlQueryInfo } from "../../client-shared/api/services.utils";
import { useSiteContext } from "../../state/SiteContext";
import { useConfigurationFilter, useOrganizationFilter, useValidityFilter } from "../../state/FilterContext";
import { ChatbotReferenceMetadata } from "../../shared/models/chatbot-references.model";
import { ChatbotMessageReferences } from "./ChatbotMessageReferences";
import { ChatbotRagBenchmark } from "./ChatbotRagBenchmark";
import { partitionChatbotChildren } from "./chatbot-children.utils";

const appAPIUrl = process.env.REACT_APP_API_URL || "/api/app";

type ChatbotUIMessage = UIMessage<ChatbotReferenceMetadata>;

export interface ChatbotProps {
    /** Optional slot rendered below the message list (ReferencesTemplate is extracted for per-message rendering). */
    children?: React.ReactNode;
    /** When true, Enter submits (Shift+Enter for newline). Matches ChatbotPrompt.sendOnEnter. */
    sendOnEnter?: boolean | string;
    /** Override API path; default `/api/app/ai/{siteName}`. */
    api?: string;
}

function getMessageText(message: UIMessage): string {
    return message.parts
        .filter((part): part is { type: "text"; text: string } => part.type === "text")
        .map((part) => part.text)
        .join("");
}

function ChatMessage({
    message,
    isLastAssistant,
    onDelete,
    onRegenerate,
    canRegenerate,
    referencesTemplateContent,
    chatStatus,
}: {
    message: ChatbotUIMessage;
    isLastAssistant: boolean;
    onDelete: () => void;
    onRegenerate: () => void;
    canRegenerate: boolean;
    referencesTemplateContent: React.ReactNode;
    chatStatus: "ready" | "submitted" | "streaming" | "error";
}) {
    const isUser = message.role === "user";
    const text = getMessageText(message);

    return (
        <Box
            component="article"
            aria-label={isUser ? "User message" : "Assistant message"}
            sx={{ mb: 2 }}
        >
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: isUser ? "action.hover" : "background.paper",
                }}
            >
                {isUser ? (
                    <Box component="p" sx={{ m: 0, whiteSpace: "pre-wrap" }}>{text}</Box>
                ) : (
                    <Box
                        component="div"
                        sx={{ "& p": { mt: 0 } }}
                        dangerouslySetInnerHTML={{ __html: text }}
                    />
                )}
            </Box>
            {!isUser && referencesTemplateContent && (
                <ChatbotMessageReferences
                    message={message}
                    templateContent={referencesTemplateContent}
                    isLastAssistant={isLastAssistant}
                    chatStatus={chatStatus}
                    messageText={text}
                />
            )}
            {!isUser ? (
                <ChatbotRagBenchmark
                    message={message}
                    isLastAssistant={isLastAssistant}
                    chatStatus={chatStatus}
                    renderMessageActions={(benchmarkIcon) => (
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                            {benchmarkIcon}
                            <Tooltip title="Delete message">
                                <IconButton size="small" aria-label="Delete message" onClick={onDelete}>
                                    <DeleteOutline fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {isLastAssistant && canRegenerate && (
                                <Tooltip title="Regenerate">
                                    <IconButton size="small" aria-label="Regenerate response" onClick={onRegenerate}>
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                />
            ) : (
                <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                    <Tooltip title="Delete message">
                        <IconButton size="small" aria-label="Delete message" onClick={onDelete}>
                            <DeleteOutline fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
}

/**
 * Multi-vendor digital assistant using Vercel AI SDK useChat with inline history.
 */
export function Chatbot({ children, sendOnEnter, api }: ChatbotProps): React.ReactElement {
    const [prompt, setPrompt] = useState("");
    const sendOnEnterEnabled = sendOnEnter === true || sendOnEnter === "true";

    const { referencesTemplateContent, otherChildren } = useMemo(
        () => partitionChatbotChildren(children),
        [children],
    );

    const { siteState } = useSiteContext();
    const organization = useOrganizationFilter();
    const configuration = useConfigurationFilter();
    const validityFilter = useValidityFilter();
    const siteName = siteState.site?.name || "";

    const filterQuery = useMemo(
        () => getUrlQueryInfo(organization, configuration, {
            validity: validityFilter,
            organization: organization || undefined,
            configuration: configuration || undefined,
        }),
        [organization, configuration, validityFilter],
    );

    const chatApi = api ?? `${appAPIUrl}/ai/${siteName}${filterQuery}`;

    const transport = useMemo(
        () => new DefaultChatTransport({ api: chatApi, credentials: "include" }),
        [chatApi],
    );

    const {
        messages,
        sendMessage,
        status,
        stop,
        regenerate,
        setMessages,
        error,
        clearError,
    } = useChat<ChatbotUIMessage>({ transport });

    const isBusy = status === "submitted" || status === "streaming";
    const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant")?.id;
    const errorMessage = useMemo(() => {
        if (!error) {
            return null;
        }
        return extractChatErrorMessage(error) || "Something went wrong. Please try again.";
    }, [error]);

    const handleSend = async () => {
        const text = prompt.trim();
        if (!text || isBusy) {
            return;
        }
        setPrompt("");
        clearError();
        await sendMessage({ text });
    };

    const handlePromptKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (!sendOnEnterEnabled || event.key !== "Enter" || event.shiftKey) {
            return;
        }
        event.preventDefault();
        void handleSend();
    };

    const handleDelete = (messageId: string) => {
        setMessages((current) => current.filter((message) => message.id !== messageId));
    };

    return (
        <Box role="region" aria-label="Chatbot" sx={{ width: "100%" }}>
            <Box
                role="log"
                aria-live="polite"
                aria-relevant="additions text"
                sx={{ mb: 2 }}
            >
                {messages.map((message) => (
                    <ChatMessage
                        key={message.id}
                        message={message}
                        isLastAssistant={message.id === lastAssistantId}
                        onDelete={() => handleDelete(message.id)}
                        onRegenerate={() => regenerate()}
                        canRegenerate={status === "ready" && !error}
                        referencesTemplateContent={referencesTemplateContent}
                        chatStatus={status}
                    />
                ))}
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => regenerate()}>
                            Try again
                        </Button>
                    }
                >
                    {errorMessage}
                </Alert>
            )}

            {otherChildren}

            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: "1em",
                }}
            >
                <TextField
                    sx={{ width: "100%" }}
                    id="chatbot-query"
                    label="Ask a question"
                    variant="standard"
                    multiline
                    minRows={3}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handlePromptKeyDown}
                    disabled={isBusy || !!error}
                    InputProps={{
                        multiline: true,
                        inputComponent: "textarea",
                    }}
                />
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, alignSelf: { sm: "flex-end" } }}>
                    {isBusy && <CircularProgress size={24} color="inherit" />}
                    {isBusy ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            aria-label="Stop generating"
                            onClick={() => stop()}
                        >
                            Stop
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            aria-label="Send message"
                            onClick={() => void handleSend()}
                            disabled={!prompt.trim() || !!error}
                        >
                            Send
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
